import {
  Avatar,
  Box,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AdminAudienceViewDto,
  RegenerateAudienceTokenDto,
  UpdateFlyerReviewAudienceDto,
} from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../../locales/messages";
import { reviewTokens } from "../../theme";
import { copyToClipboard } from "../copyToClipboard";
import { ExpiryChip } from "./ExpiryChip";
import { RegenerateLinkPopover } from "./RegenerateLinkPopover";

interface Props {
  audience: AdminAudienceViewDto;
  onUpdate: (
    audienceId: string,
    body: UpdateFlyerReviewAudienceDto,
  ) => Promise<boolean | undefined>;
  onRegenerate: (
    audienceId: string,
    body: RegenerateAudienceTokenDto,
  ) => Promise<{ url: string; expiresAt?: string } | undefined>;
  onRevoke: (audienceId: string) => Promise<boolean | undefined>;
}

const getInitials = (displayName: string, fallback: string) => {
  const source = (displayName || fallback || "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

export function AudienceRow({
  audience,
  onUpdate,
  onRegenerate,
  onRevoke,
}: Props) {
  const { t } = useTranslation();
  const [permission, setPermission] = useState(audience.permission);
  const [latestUrl, setLatestUrl] = useState<string | undefined>(audience.url);
  const [showUrl, setShowUrl] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | string | undefined>(
    audience.tokenExpiresAt,
  );
  const [regenAnchor, setRegenAnchor] = useState<HTMLElement | null>(null);
  const isRevoked =
    audience.permission === AdminAudienceViewDto.PermissionEnum.REVOKED;

  const handlePermissionChange = async (
    next: UpdateFlyerReviewAudienceDto.PermissionEnum,
  ) => {
    setPermission(next as unknown as AdminAudienceViewDto.PermissionEnum);
    await onUpdate(audience.id, { permission: next });
  };

  const handleRegenerate = async (expiresAtIso: string | null) => {
    const result = await onRegenerate(audience.id, {
      expiresAt: expiresAtIso as unknown as string,
    });
    if (result?.url) {
      setLatestUrl(result.url);
      setShowUrl(true);
    }
    setExpiresAt(result?.expiresAt ?? expiresAtIso ?? undefined);
  };

  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        borderTop: 1,
        borderColor: "divider",
        opacity: isRevoked ? 0.55 : 1,
        transition: "background-color 0.15s ease",
        "&:hover": {
          bgcolor: reviewTokens.slate50,
        },
        "&:first-of-type": {
          borderTop: "none",
        },
      }}
      data-testid={`audience-row-${audience.audienceKey}`}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: 12,
            fontWeight: 600,
            bgcolor: reviewTokens.slate100,
            color: reviewTokens.slate600,
            border: `1px solid ${reviewTokens.border}`,
          }}
        >
          {getInitials(audience.displayName, audience.audienceKey)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={audience.displayName}
            >
              {audience.displayName || audience.audienceKey}
            </Typography>
            {isRevoked ? (
              <Chip
                size="small"
                label="Revoked"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 600,
                  bgcolor: reviewTokens.slate100,
                  color: reviewTokens.slate600,
                }}
              />
            ) : null}
            <ExpiryChip
              expiresAt={expiresAt}
              testId={`audience-expiry-${audience.audienceKey}`}
            />
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              fontSize: 11,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={audience.audienceKey}
          >
            <Box
              component="span"
              sx={{
                fontFamily: reviewTokens.fontMono,
                color: "text.secondary",
              }}
            >
              {audience.audienceKey}
            </Box>
            {audience.email ? ` · ${audience.email}` : ""}
          </Typography>
        </Box>
        <TextField
          size="small"
          select
          value={permission}
          disabled={isRevoked}
          onChange={(e) =>
            handlePermissionChange(
              e.target
                .value as unknown as UpdateFlyerReviewAudienceDto.PermissionEnum,
            )
          }
          sx={{
            width: 100,
            "& .MuiOutlinedInput-root": {
              fontSize: 12,
              fontWeight: 500,
            },
          }}
          data-testid={`audience-permission-${audience.audienceKey}`}
        >
          <MenuItem
            value={
              UpdateFlyerReviewAudienceDto.PermissionEnum.VIEW as unknown as string
            }
          >
            VIEW
          </MenuItem>
          <MenuItem
            value={
              UpdateFlyerReviewAudienceDto.PermissionEnum.EDIT as unknown as string
            }
          >
            EDIT
          </MenuItem>
          {isRevoked ? (
            <MenuItem value={"REVOKED"} disabled>
              REVOKED
            </MenuItem>
          ) : null}
        </TextField>
        <Stack direction="row" spacing={0.25}>
          {latestUrl ? (
            <Tooltip title={showUrl ? "Hide link" : "Show link"}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => setShowUrl((v) => !v)}
                  data-testid={`audience-show-url-${audience.audienceKey}`}
                  sx={{
                    color: showUrl ? "primary.main" : "text.secondary",
                  }}
                >
                  <LinkIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          <Tooltip title={t(messages.Copy_Link())}>
            <span>
              <IconButton
                size="small"
                disabled={!latestUrl}
                onClick={() =>
                  latestUrl &&
                  copyToClipboard(latestUrl, t(messages.Copied()))
                }
                data-testid={`audience-copy-${audience.audienceKey}`}
                sx={{ color: "text.secondary" }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t(messages.Regenerate_Link())}>
            <span>
              <IconButton
                size="small"
                disabled={isRevoked}
                onClick={(e) => setRegenAnchor(e.currentTarget)}
                data-testid={`audience-regenerate-${audience.audienceKey}`}
                sx={{
                  color: regenAnchor ? "primary.main" : "text.secondary",
                }}
              >
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t(messages.Revoke())}>
            <span>
              <IconButton
                size="small"
                disabled={isRevoked}
                onClick={() => onRevoke(audience.id)}
                data-testid={`audience-revoke-${audience.audienceKey}`}
                sx={{
                  color: "error.main",
                  "&:hover": { bgcolor: "rgba(220, 38, 38, 0.08)" },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {showUrl && latestUrl ? (
        <Box
          sx={{
            mt: 1,
            ml: 5.5,
            p: 1,
            borderRadius: `${reviewTokens.radiusSm}px`,
            bgcolor: reviewTokens.bgMuted,
            border: `1px solid ${reviewTokens.border}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              wordBreak: "break-all",
              fontFamily: reviewTokens.fontMono,
              fontSize: 11,
              color: "text.secondary",
            }}
          >
            {latestUrl}
          </Typography>
        </Box>
      ) : null}

      <RegenerateLinkPopover
        open={Boolean(regenAnchor)}
        anchorEl={regenAnchor}
        currentExpiresAt={expiresAt}
        onClose={() => setRegenAnchor(null)}
        onConfirm={(iso) => handleRegenerate(iso)}
      />
    </Box>
  );
}
